import React, { useState, useEffect } from 'react';
import { X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import type { ApprovalWorkflow, WorkflowAuditLog } from '@/types/workflow';
import { getWorkflowHistory } from '@/services/workflowService';

interface WorkflowHistoryProps {
  workflow: ApprovalWorkflow;
  onClose: () => void;
}

export function WorkflowHistory({ workflow, onClose }: WorkflowHistoryProps) {
  const [history, setHistory] = useState<WorkflowAuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [workflow.id]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const data = await getWorkflowHistory(workflow.id);
      setHistory(data);
    } catch (error) {
      toast.error('Failed to load workflow history');
      console.error('Load history error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionBadge = (action: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      created: { variant: 'default', label: 'Created' },
      updated: { variant: 'default', label: 'Updated' },
      activated: { variant: 'default', label: 'Activated' },
      deactivated: { variant: 'secondary', label: 'Deactivated' },
      deleted: { variant: 'destructive', label: 'Deleted' },
      stage_added: { variant: 'default', label: 'Stage Added' },
      stage_removed: { variant: 'secondary', label: 'Stage Removed' },
      stage_updated: { variant: 'default', label: 'Stage Updated' },
      approver_added: { variant: 'default', label: 'Approver Added' },
      approver_removed: { variant: 'secondary', label: 'Approver Removed' },
    };

    const config = variants[action] || { variant: 'outline', label: action };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Workflow History</h2>
          <p className="text-muted-foreground">{workflow.name}</p>
        </div>
        <Button variant="outline" onClick={onClose}>
          <X className="h-4 w-4 mr-2" />
          Close
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Change Log</CardTitle>
          <CardDescription>
            Complete history of changes made to this workflow
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              Loading history...
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mb-2 opacity-50" />
              <p>No history available</p>
            </div>
          ) : (
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-4">
                {history.map((log) => (
                  <div key={log.id} className="flex gap-4 pb-4 border-b last:border-0">
                    <div className="flex-shrink-0 w-24">
                      {getActionBadge(log.action)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <p className="font-medium">{log.changeDetails}</p>
                          <p className="text-sm text-muted-foreground">
                            by {log.changedByName || 'Unknown'}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {format(new Date(log.changedAt), 'MMM d, yyyy HH:mm')}
                        </div>
                      </div>
                      {log.oldValue && log.newValue && (
                        <div className="mt-2 p-2 bg-muted rounded text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="font-medium">Old:</span> {log.oldValue}
                            </div>
                            <div>
                              <span className="font-medium">New:</span> {log.newValue}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="mt-1 text-xs text-muted-foreground">
                        Version {log.version}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
